import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import {
  IS_WHITE_LABEL_BRAND,
  REBRAND_BANNER_HEIGHT,
} from "../../utils/constants";
import {
  AUTH_LAYOUT_FEATURE_AI_DESCRIPTION,
  AUTH_LAYOUT_FEATURE_AI_TITLE,
  AUTH_LAYOUT_FEATURE_ANALYTICS_DESCRIPTION,
  AUTH_LAYOUT_FEATURE_ANALYTICS_TITLE,
  AUTH_LAYOUT_FEATURE_SCHEDULING_DESCRIPTION,
  AUTH_LAYOUT_FEATURE_SCHEDULING_TITLE,
  AUTH_LAYOUT_HERO_DESCRIPTION,
  AUTH_LAYOUT_HERO_TITLE,
  AUTH_LAYOUT_PREVIEW_IMAGE_ALT,
} from "../../i18n/keysTranslations";
import { BORDER_GRAY_COLOR } from "../../utils/colors";
import appPreviewImage from "../../assets/img/app.png";

const CAL_BREAKPOINT_2XL = 1536;
const MEDIA_CARD = `@media (min-width: ${CAL_BREAKPOINT_2XL}px)`;

const AuthLayout = ({
  children,
  showFeatures = true,
  fitCardToContent = false,
  cardCornerContent = null,
}) => {
  const theme = useTheme();
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));
  const showMarketingPanel =
    showFeatures && !isBelowLg && !IS_WHITE_LABEL_BRAND;

  const featureItems = [
    {
      icon: (
        <AutoAwesomeIcon
          sx={{
            fontSize: "clamp(20px, 1.4vh + 14px, 25px)",
            color: "text.secondary",
            mb: 0.5,
          }}
        />
      ),
      titleKey: AUTH_LAYOUT_FEATURE_AI_TITLE,
      descriptionKey: AUTH_LAYOUT_FEATURE_AI_DESCRIPTION,
    },
    {
      icon: (
        <TrendingUpIcon
          sx={{
            fontSize: "clamp(20px, 1.4vh + 14px, 25px)",
            color: "text.secondary",
            mb: 0.5,
          }}
        />
      ),
      titleKey: AUTH_LAYOUT_FEATURE_ANALYTICS_TITLE,
      descriptionKey: AUTH_LAYOUT_FEATURE_ANALYTICS_DESCRIPTION,
    },
    {
      icon: (
        <CalendarTodayIcon
          sx={{
            fontSize: "clamp(20px, 1.4vh + 14px, 25px)",
            color: "text.secondary",
            mb: 0.5,
          }}
        />
      ),
      titleKey: AUTH_LAYOUT_FEATURE_SCHEDULING_TITLE,
      descriptionKey: AUTH_LAYOUT_FEATURE_SCHEDULING_DESCRIPTION,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: `calc(100dvh - ${REBRAND_BANNER_HEIGHT}px)`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        ...(fitCardToContent
          ? {
              bgcolor: "background.default",
            }
          : {
              backgroundColor: "#f6f7f9",
              [MEDIA_CARD]: {
                backgroundColor: theme.palette.background.default,
              },
            }),
      }}
    >
      <Box
        sx={{
          display: "grid",
          alignItems: "stretch",
          gridTemplateColumns: showMarketingPanel
            ? { xs: "1fr", lg: "1fr 1fr" }
            : "1fr",
          overflowX: "hidden",
          overflowY: "visible",
          ...(fitCardToContent
            ? {
                position: "relative",
                backgroundColor: "#f6f7f9",
                width: "fit-content",
                maxWidth: "min(1440px, calc(100% - 32px))",
                mx: "auto",
                border: `1px solid ${BORDER_GRAY_COLOR}`,
                borderRadius: "20px",
                py: "clamp(16px, 2vh, 24px)",
                minHeight: "unset",
                maxHeight: "unset",
                gridTemplateRows: "auto",
              }
            : {
                backgroundColor: "#f6f7f9",
                width: "100%",
                maxWidth: 1440,
                gridTemplateRows: "minmax(0, auto)",
                [MEDIA_CARD]: {
                  border: `1px solid ${BORDER_GRAY_COLOR}`,
                  borderRadius: "20px",
                  py: "clamp(12px, 1.4vh + 6px, 24px)",
                  minHeight: `calc(100dvh - ${REBRAND_BANNER_HEIGHT}px - clamp(24px, 3.5vh, 56px))`,
                  maxHeight: `calc(100dvh - ${REBRAND_BANNER_HEIGHT}px - clamp(8px, 1.2vh, 20px))`,
                  overflowY: "auto",
                  gridTemplateRows: "minmax(0, 1fr)",
                },
              }),
        }}
      >
        {cardCornerContent ? (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 12, sm: 16 },
              right: { xs: 12, sm: 16 },
              zIndex: 1,
            }}
          >
            {cardCornerContent}
          </Box>
        ) : null}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: fitCardToContent ? "flex-start" : "center",
            width: "100%",
            minWidth: 0,
            minHeight: fitCardToContent ? "auto" : "100%",
            pt: fitCardToContent
              ? "clamp(12px, 1vh + 8px, 20px)"
              : "clamp(16px, 1.2vh + 10px, 24px)",
            pb: fitCardToContent
              ? "clamp(12px, 1vh + 8px, 20px)"
              : "clamp(18px, 1.4vh + 12px, 32px)",
            px: fitCardToContent
              ? { xs: 2, sm: 3, md: 4 }
              : { xs: 2, sm: 4, md: 5, lg: 7, xl: 7 },
          }}
        >
          <Box
            sx={{
              width: fitCardToContent ? "auto" : "100%",
              maxWidth: fitCardToContent ? "100%" : 576,
            }}
          >
            {children}
          </Box>
        </Box>

        {showMarketingPanel && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderTop: `1px solid ${BORDER_GRAY_COLOR}`,
              borderLeft: `1px solid ${BORDER_GRAY_COLOR}`,
              borderBottom: `1px solid ${BORDER_GRAY_COLOR}`,
              borderRight: "none",
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              bgcolor: "#eeeff2",
              py: "clamp(20px, 2.8vh + 10px, 48px)",
              pl: { lg: "clamp(24px, 1.8vw + 16px, 48px)" },
              minHeight: "100%",
            }}
          >
            <Box>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: "clamp(1.375rem, 0.9vh + 1.05rem, 1.75rem)",
                  lineHeight: 1,
                  color: "text.primary",
                  mb: "clamp(6px, 0.6vh + 4px, 10px)",
                }}
              >
                {t(AUTH_LAYOUT_HERO_TITLE)}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  fontSize: "1rem",
                  lineHeight: 1.25,
                  mb: "clamp(14px, 1.6vh + 8px, 32px)",
                }}
              >
                {t(AUTH_LAYOUT_HERO_DESCRIPTION)}
              </Typography>

              <Box
                sx={{
                  display: { xs: "none", lg: "block" },
                  borderStyle: "dashed",
                  borderWidth: 1,
                  borderColor: "#d3d6dc",
                  borderRight: "none",
                  borderTopLeftRadius: 12,
                  borderBottomLeftRadius: 12,
                  bgcolor: "#e8e9eb",
                  pt: 1,
                  pb: 1,
                  pl: 1,
                  mb: "clamp(12px, 1.3vh + 6px, 24px)",
                }}
              >
                <Box
                  component="img"
                  src={appPreviewImage}
                  alt={t(AUTH_LAYOUT_PREVIEW_IMAGE_ALT, { NAME_BUSINESS })}
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                    verticalAlign: "bottom",
                    maxHeight: "min(580px, max(200px, calc(100dvh - 320px)))",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: { xs: "none", lg: "grid" },
                  gridTemplateColumns: {
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: {
                    lg: "clamp(10px, 0.9vh + 6px, 16px)",
                    xl: "clamp(12px, 1vh + 8px, 24px)",
                  },
                  mt: "clamp(8px, 0.8vh + 6px, 16px)",
                }}
              >
                {featureItems.map((feature) => (
                  <Box
                    key={feature.titleKey}
                    sx={{
                      maxWidth: 208,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      lineHeight: 1,
                    }}
                  >
                    <Box sx={{ color: "text.primary" }}>{feature.icon}</Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 0.5,
                      }}
                    >
                      {t(feature.titleKey)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.43,
                      }}
                    >
                      {t(feature.descriptionKey)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AuthLayout;
